import React, { useContext, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { WebsiteContext } from "../../context/AppContext";
import { firestore } from "../../firebase/firebase";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import "../../styles/blogDetails.css";
import Loader from "react-js-loader";
import HelmetComponent from "../common/HelmetComponent";
import { curatedBlogDetails } from "../../data/blogDetailsData";
import {
  IoArrowBack,
  IoCalendarOutline,
  IoTimeOutline,
  IoPersonOutline,
  IoSparkles,
} from "react-icons/io5";
import { FaGooglePlay } from "react-icons/fa6";
import { motion } from "framer-motion";

const BlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { color, dark, handleRoute } = useContext(WebsiteContext);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadArticleData = useCallback(async () => {
    // 1. Check if ID exists in our curated articles dictionary
    if (curatedBlogDetails[id]) {
      setArticle(curatedBlogDetails[id]);
      setLoading(false);
      return;
    }

    // 2. Otherwise attempt Firestore fetch
    try {
      // Check main blog doc
      const blogDocRef = doc(firestore, "blogs", id);
      const blogDocSnap = await getDoc(blogDocRef);

      // Check subcollection blogs/{id}/blog_details
      const subcollectionRef = collection(firestore, `blogs/${id}/blog_details`);
      const subSnapshot = await getDocs(subcollectionRef);

      if (blogDocSnap.exists() || !subSnapshot.empty) {
        const mainData = blogDocSnap.exists() ? blogDocSnap.data() : {};
        const subData = subSnapshot.docs.map((d) => d.data());

        // Extract cover image and title safely
        const coverImage =
          mainData.cover_picture ||
          subData.find((s) => s.cover_picture)?.cover_picture ||
          curatedBlogDetails["default-1"].coverImage;

        const title =
          mainData.title ||
          subData.find((s) => s.blog_title || s.title)?.blog_title ||
          "English Fluency Article";

        const sections =
          subData.length > 0
            ? subData.map((s, idx) => ({
                heading: s.title || `Section ${idx + 1}`,
                body: s.content || "",
              }))
            : [
                {
                  heading: "Overview",
                  body: mainData.content || mainData.excerpt || "Article content details.",
                },
              ];

        setArticle({
          id,
          title,
          category: mainData.category || "English Mastery",
          author: mainData.author || "Fluent Fable Mentor",
          date: mainData.formattedDate || "Recent",
          readingTime: `${mainData.time_to_read || 5} min read`,
          coverImage,
          sections,
          takeaway: mainData.takeaway || "Consistent practice transforms language hesitation into natural fluency.",
        });
      } else {
        // Fallback to first curated article
        setArticle(curatedBlogDetails["default-1"]);
      }
    } catch (err) {
      console.warn("Firestore fetch error, using curated article fallback:", err);
      setArticle(curatedBlogDetails["default-1"]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadArticleData();
  }, [loadArticleData]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "120px 0" }}>
        <Loader type="spinner-cub" size={40} bgColor={color} color={color} />
      </div>
    );
  }

  if (!article) return null;

  return (
    <>
      <HelmetComponent
        title={`${article.title} | Fluent Fable Blog`}
        name="description"
        content={article.title}
      />

      <div className={`blog_details_page_wrapper ${dark ? "details_dark" : ""}`}>
        {/* Back Link */}
        <button className="blog_back_btn" onClick={() => navigate("/blogs")}>
          <IoArrowBack size={16} />
          <span>Back to all articles</span>
        </button>

        {/* Article Header */}
        <div className="article_header">
          <span className="article_category_pill">{article.category}</span>
          <h1 className="article_main_title" style={{ color: color }}>
            {article.title}
          </h1>

          <div className="article_meta_row" style={{ color: color }}>
            <span className="meta_item">
              <IoPersonOutline size={15} /> {article.author}
            </span>
            <span className="meta_item">
              <IoCalendarOutline size={15} /> {article.date}
            </span>
            <span className="meta_item">
              <IoTimeOutline size={15} /> {article.readingTime}
            </span>
          </div>
        </div>

        {/* Hero Cover Image */}
        <div className="article_hero_cover">
          <img src={article.coverImage} alt={article.title} />
        </div>

        {/* Article Sections */}
        <div className="article_body_content">
          {article.sections.map((section, idx) => (
            <div key={idx} className="article_section_block">
              {section.heading && (
                <h2 className="section_heading">{section.heading}</h2>
              )}
              <p className="section_text" style={{ color: color }}>
                {section.body}
              </p>
            </div>
          ))}

          {/* Key Takeaway Callout */}
          {article.takeaway && (
            <div className="takeaway_box">
              <h3 className="takeaway_title" style={{ color: color }}>
                <IoSparkles color="#f59e0b" size={18} /> Key Takeaway
              </h3>
              <p className="takeaway_text" style={{ color: color }}>
                "{article.takeaway}"
              </p>
            </div>
          )}
        </div>

        {/* Bottom CTA Banner */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="article_bottom_cta"
        >
          <h3 className="bottom_cta_title">Put These Lessons Into Practice</h3>
          <p className="bottom_cta_desc">
            Reading about fluency is great, but speaking is where true mastery happens. Try interactive daily quizzes, vocabulary insights, and 24/7 AI conversation practice with Fluent Fable.
          </p>
          <button
            className="bottom_cta_btn"
            onClick={() => handleRoute("GettheApp")}
          >
            <FaGooglePlay size={16} />
            <span>Download Fluent Fable App</span>
          </button>
        </motion.div>
      </div>
    </>
  );
};

export default BlogDetails;
